import { FocusKeyManager, FocusOrigin } from '@angular/cdk/a11y';
import { Direction } from '@angular/cdk/bidi';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
    ESCAPE,
    DOWN_ARROW,
    UP_ARROW,
    hasModifierKey,
} from '@angular/cdk/keycodes';
import { AnimationEvent } from '@angular/animations';
import {
    AfterContentInit,
    ChangeDetectionStrategy,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    Inject,
    InjectionToken,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    TemplateRef,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { merge, Subject, Subscription } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';
import { menuAnimation } from './menu-animations';
import { MenuItemComponent } from './menu-item.component';
import { ICC_MENU_PANEL} from './menu-panel';
import type { MenuPanel, MenuPositionX, MenuPositionY } from './menu-panel';


export interface MenuDefaultOptions {
    /** The x-axis position of the menu. */
    xPosition: MenuPositionX;

    /** The y-axis position of the menu. */
    yPosition: MenuPositionY;

    /** Whether the menu should overlap the menu trigger. */
    overlapTrigger: boolean;

    /** Class to be applied to the menu's backdrop. */
    backdropClass: string;

    /** Class or list of classes to be applied to the menu's overlay panel. */
    overlayPanelClass?: string | string[];

    /** Whether the menu has a backdrop. */
    hasBackdrop?: boolean;

    /** Index of the item to be focused */
    indexFocusItem?: number;

}

/** Injection token to be used to override the default options for `mat-menu`. */
export const ICC_MENU_DEFAULT_OPTIONS = new InjectionToken<MenuDefaultOptions>(
    'icc-menu-default-options',
    {
        providedIn: 'root',
        factory: ICC_MENU_DEFAULT_OPTIONS_FACTORY,
    }
);

/** @docs-private */
export function ICC_MENU_DEFAULT_OPTIONS_FACTORY(): MenuDefaultOptions {
    return {
        overlapTrigger: false,
        xPosition: 'after',
        yPosition: 'below',
        backdropClass: 'cdk-overlay-transparent-backdrop',
        hasBackdrop: true
    };
}

/** Reason why the menu was closed. */
export type MenuCloseReason = void | 'click' | 'keydown' | 'tab';

let menuPanelUid = 0;

@Component({
    selector: 'icc-menu',
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    exportAs: 'iccMenu',
    providers: [{ provide: ICC_MENU_PANEL, useExisting: MenuComponent }],
    animations: [menuAnimation],
})
export class MenuComponent implements OnInit, MenuPanel<any>, AfterContentInit, OnDestroy {
    private _keyManager!: FocusKeyManager<MenuItemComponent>;
    private _xPosition: MenuPositionX = this._defaultOptions.xPosition || 'after';
    private _yPosition: MenuPositionY = this._defaultOptions.yPosition || 'below';
    private _indexFocusItem: number|null = null;

    /** All items inside the menu. Includes items nested inside another menu. */
    @ContentChildren(MenuItemComponent, { descendants: true })
    _allItems!: QueryList<MenuItemComponent>;

    /** Only the direct descendant menu items. */
    private _directDescendantItems = new QueryList<MenuItemComponent>();

    /** Config object to be passed into the menu's ngClass */
    _classList: { [key: string]: boolean } = {};

    /** Layout direction of the menu. */
    direction?: Direction;

    /** Class or list of classes to be added to the overlay panel. */
    overlayPanelClass: string | string[] = this._defaultOptions.overlayPanelClass || '';

    /** Class to be added to the backdrop element. */
    @Input() backdropClass: string = this._defaultOptions.backdropClass || '';

    /** aria-label for the menu panel. */
    @Input('aria-label') ariaLabel?: string;

    /** aria-labelledby for the menu panel. */
    @Input('aria-labelledby') ariaLabelledby?: string;

    /** aria-describedby for the menu panel. */
    @Input('aria-describedby') ariaDescribedby?: string;

    /** Position of the menu in the X axis. */
    @Input()
    get xPosition(): MenuPositionX {
        return this._xPosition;
    }
    set xPosition(value: MenuPositionX) {
        this._xPosition = value;
        this.setPositionClasses();
    }

    /** Position of the menu in the Y axis. */
    @Input()
    get yPosition(): MenuPositionY {
        return this._yPosition;
    }
    set yPosition(value: MenuPositionY) {
        this._yPosition = value;
        this.setPositionClasses();
    }

    /** Current state of the panel animation. */
    _panelAnimationState: 'void' | 'fadeInTop' = 'void';

    /** Emits whenever an animation on the menu completes. */
    readonly _animationDone = new Subject<AnimationEvent>();

    /** Whether the menu is animating. */
    _isAnimating = false;

    /** @docs-private */
    @ViewChild(TemplateRef) templateRef!: TemplateRef<any>;

    /** Event emitted when the menu is closed. */
    @Output() closed = new EventEmitter<void | 'click' | 'keydown' | 'tab'>();
    @Output() index = new EventEmitter<number|null>();

    /** Whether the menu should overlap its trigger. */
    @Input()
    get overlapTrigger(): boolean {
        return this._overlapTrigger;
    }
    set overlapTrigger(value: boolean) {
        this._overlapTrigger = coerceBooleanProperty(value);
    }
    private _overlapTrigger: boolean = this._defaultOptions.overlapTrigger;

    /** Whether the menu has a backdrop. */
    @Input()
    get hasBackdrop(): boolean | undefined {
        return this._hasBackdrop;
    }
    set hasBackdrop(value: boolean | undefined) {
        this._hasBackdrop = coerceBooleanProperty(value);
    }
    private _hasBackdrop: boolean | undefined = this._defaultOptions.hasBackdrop;

    @Input()
    get indexFocusItem(): number | null {
        return this._indexFocusItem;
    }
    set indexFocusItem(indexFocusItem:number| null) {
        this._indexFocusItem = indexFocusItem;
    }


    /** Subscription to tab events on the menu panel */
    private _tabSubscription = Subscription.EMPTY;

    /**
     * This method takes classes set on the host mat-menu element and applies them on the
     * menu template that displays in the overlay container.  Otherwise, it's difficult
     * to style the containing menu from outside the component.
     * @param classes list of class names
     */
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('class')
    set panelClass(classes: string) {
        const previousPanelClass = this._previousPanelClass;

        if (previousPanelClass && previousPanelClass.length) {
            previousPanelClass.split(' ').forEach((className: string) => {
                this._classList[className] = false;
            });
        }

        this._previousPanelClass = classes;

        if (classes && classes.length) {
            classes.split(' ').forEach((className: string) => {
                this._classList[className] = true;
            });

            this._elementRef.nativeElement.className = '';
        }
    }
    private _previousPanelClass!: string;

    readonly panelId = `icc-menu-panel-${menuPanelUid++}`;

    constructor(
        private _elementRef: ElementRef<HTMLElement>,
        @Inject(ICC_MENU_DEFAULT_OPTIONS) private _defaultOptions: MenuDefaultOptions
    ) {}

    ngOnInit(): void {
        this.setPositionClasses();
    }

    ngAfterContentInit() {
        this._updateDirectDescendants();
        this._keyManager = new FocusKeyManager(this._directDescendantItems)
            .withWrap()
            .withTypeAhead()
            .withHomeAndEnd();
        this._tabSubscription = this._keyManager.tabOut.subscribe(() => this.emitClosed('tab'));

        // If a user manually (programmatically) focuses a menu item, we need to reflect that focus
        // change back to the key manager. Note that we don't need to unsubscribe here because _focused
        // is internal and we know that it gets completed on destroy.
        this._directDescendantItems.changes
            .pipe(
                startWith(this._directDescendantItems),
                switchMap((items) =>
                    merge<MenuItemComponent[]>(
                        ...items.map((item: MenuItemComponent) => item._focused)
                    )
                )
            )
            .subscribe((focusedItem) => this._keyManager.updateActiveItem(focusedItem));
    }

    ngOnDestroy() {
        this._directDescendantItems.destroy();
        this._tabSubscription.unsubscribe();
        this.closed.complete();
        this.index.complete();
    }

    /**
     * Adds classes to the menu panel based on its position. Can be used by
     * consumers to add specific styling based on the position.
     * @param posX Position of the menu along the x axis.
     * @param posY Position of the menu along the y axis.
     * @docs-private
     */
    setPositionClasses(posX: MenuPositionX = this.xPosition, posY: MenuPositionY = this.yPosition) {
        const classes = this._classList;
        classes['icc-menu-before'] = posX === 'before';
        classes['icc-menu-after'] = posX === 'after';
        classes['icc-menu-above'] = posY === 'above';
        classes['icc-menu-below'] = posY === 'below';
    }

    /** Handle a keyboard event from the menu, delegating to the appropriate action. */
    _handleKeydown(event: KeyboardEvent) {
        const keyCode = event.keyCode;
        const manager = this._keyManager;

        switch (keyCode) {
            case ESCAPE:
                if (!hasModifierKey(event)) {
                    event.preventDefault();
                    this.emitClosed('keydown');
                }
                break;
            default:
                if (keyCode === UP_ARROW || keyCode === DOWN_ARROW) {
                    manager.setFocusOrigin('keyboard');
                }

                manager.onKeydown(event);
        }
    }

    /**
     * Focus the first item in the menu.
     * @param origin Action from which the focus originated. Used to set the correct styling.
     */
    focusItem(origin: FocusOrigin = 'program'): void {
        if(this.indexFocusItem !== null){
            this._focusIndexItem(this.indexFocusItem);
        }else{
            this._focusFirstItem(origin);
        }
    }

    /**
     * Actual implementation that focuses the first item. Needs to be separated
     * out so we don't repeat the same logic in the public `focusFirstItem` method.
     */
    private _focusFirstItem(origin: FocusOrigin) {
        const manager = this._keyManager;

        manager.setFocusOrigin(origin).setFirstItemActive();

        // If there's no active item at this point, it means that all the items are disabled.
        // Move focus to the menu panel so keyboard events like Escape still work. Also this will
        // give _some_ feedback to screen readers.
        if (!manager.activeItem && this._directDescendantItems.length) {
            let element = this._directDescendantItems.first._getHostElement().parentElement;

            // Because the `mat-menu` is at the DOM insertion point, not inside the overlay, we don't
            // have a nice way of getting a hold of the menu panel. We can't use a `ViewChild` either
            // because the panel is inside an `ng-template`. We work around it by starting from one of
            // the items and walking up the DOM.
            while (element) {
                if (element.getAttribute('role') === 'menu') {
                    element.focus();
                    break;
                } else {
                    element = element.parentElement;
                }
            }
        }
    }

    private _focusIndexItem (indexFocusItem:number){

            const items = this._directDescendantItems.filter((e, i) => i === indexFocusItem);
            if(items[0]){
                items[0].focus();
            }
    }

    /** Starts the enter animation. */
    _startAnimation() {
        this._panelAnimationState = 'fadeInTop';
    }

    /** Callback that is invoked when the panel animation completes. */
    _onAnimationDone(event: AnimationEvent) {
        this._animationDone.next(event);
        this._isAnimating = false;
    }

    /** Resets the panel animation to its initial state. */
    _resetAnimation() {
        this._panelAnimationState = 'void';
    }

    _onAnimationStart(event: AnimationEvent) {
        this._isAnimating = true;

        // Scroll the content element to the top as soon as the animation starts.
        if (event.toState === 'enter' && this._keyManager.activeItemIndex === 0) {
            event.element.scrollTop = 0;
        }
    }

    /**
     * Resets the active item in the menu. This is used when the menu is opened, allowing
     * the user to start from the first option when pressing the down arrow.
     */
    resetActiveItem() {
        this._keyManager.setActiveItem(-1);
    }

    /**
     * Sets up a stream that will keep track of any newly-added menu items and will update the list
     * of direct descendants. We collect the descendants this way, because `_allItems` can include
     * items that are part of child menus, and using a custom way of registering items is unreliable
     * when it comes to maintaining the item order.
     */
    private _updateDirectDescendants() {
        this._allItems.changes
            .pipe(startWith(this._allItems))
            .subscribe((items: QueryList<MenuItemComponent>) => {
                this._directDescendantItems.reset(
                    items.filter((item) => item._parentMenu === this)
                );
                this._directDescendantItems.notifyOnChanges();
            });
    }

    private emitClosed(key:'click' | 'keydown' | 'tab'){
        this.closed.emit(key);
        this.index.emit(this.indexFocusItem);
    }

    setIndexItem(index:number|null){
        this.indexFocusItem = index;
    }

    getChildIndex(node:any):number | null {
        return node?.parentNode?.childNodes ? Array.prototype.indexOf.call(node.parentNode.childNodes, node) : null;
    }

}
